import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SavingsIcon from "@mui/icons-material/Savings";
import {
  useGetPaymentHistoryQuery,
  Transaction,
} from "components/Slices/usersApiSlice";

type FilterType = "all" | "DEPOSIT" | "WITHDRAWAL";

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const TransactionRow = ({ transaction }: { transaction: Transaction }) => {
  const theme = useTheme();
  const isDeposit = transaction.type === "DEPOSIT";
  // A contribution is routine savings, not a loss — keep it calm green-on-
  // neutral. Red is reserved for genuinely failed/overdue states (the chip).
  const iconColor = isDeposit
    ? theme.palette.primary.main
    : theme.palette.success.main;
  const amountColor = isDeposit
    ? theme.palette.text.primary
    : theme.palette.success.dark;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.75,
        px: 0,
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 2,
          mx: -1,
          px: 1,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            bgcolor: alpha(iconColor, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isDeposit ? (
            <ArrowUpwardIcon sx={{ fontSize: "1rem", color: iconColor }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: "1rem", color: iconColor }} />
          )}
        </Box>
        <Box>
          <Typography
            sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}
          >
            {transaction.description}
          </Typography>
          <Typography
            sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.1 }}
          >
            <a
              href={`/circles/${transaction.circleId}`}
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              {transaction.circleName || "N/A"}
            </a>{" "}
            · {formatDate(transaction.date)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography
          sx={{ fontSize: "0.9rem", fontWeight: 700, color: amountColor }}
        >
          {isDeposit ? "−" : "+"}
          {formatCurrency(transaction.amount, transaction.currency)}
        </Typography>
        <Chip
          label={
            transaction.status.charAt(0).toUpperCase() +
            transaction.status.slice(1).toLowerCase()
          }
          size="small"
          color={
            transaction.status === "COMPLETED"
              ? "success"
              : transaction.status === "PENDING"
                ? "warning"
                : "error"
          }
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: "0.78rem",
            fontWeight: 600,
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      </Box>
    </Box>
  );
};

const SummaryCard = ({
  label,
  value,
  icon,
  paletteColor,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  paletteColor: string;
}) => (
  <Box
    sx={{
      flex: 1,
      borderRadius: 3,
      bgcolor: alpha(paletteColor, 0.07),
      border: "1px solid",
      borderColor: alpha(paletteColor, 0.16),
      p: 2.5,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Box sx={{ color: paletteColor, display: "flex", fontSize: "1rem" }}>
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: "0.78rem",
          fontWeight: 700,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{ fontSize: "1.4rem", fontWeight: 700, color: "text.primary" }}
    >
      {value}
    </Typography>
  </Box>
);

export function PaymentHistoryForm() {
  const theme = useTheme();
  const [filter, setFilter] = useState<FilterType>("all");
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, isLoading, isFetching } = useGetPaymentHistoryQuery({
    type: filter,
    cursor,
    limit: 20,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: "background.paper",
          boxShadow:
            "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
          p: 5,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const { transactions = [], summary, nextCursor } = data || {};
  // Summary tiles were hardcoded to USD while rows used each transaction's
  // currency — the same screen showed "$250.00" above "−CA$50.00".
  const summaryCurrency = transactions[0]?.currency || "USD";

  return (
    <Box
      sx={{
        borderRadius: 4,
        bgcolor: "background.paper",
        boxShadow:
          "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <HistoryIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
          </Box>
          <Typography
            sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}
          >
            Payment History
          </Typography>
        </Box>

        <Select
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as FilterType);
            setCursor(undefined);
          }}
          size="small"
          sx={{
            minWidth: 160,
            fontSize: "0.82rem",
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
            "& .MuiSelect-select": { py: 0.75 },
            borderRadius: 2,
          }}
        >
          <MenuItem value="all" sx={{ fontSize: "0.82rem" }}>
            All Transactions
          </MenuItem>
          <MenuItem value="DEPOSIT" sx={{ fontSize: "0.82rem" }}>
            Contributions Only
          </MenuItem>
          <MenuItem value="WITHDRAWAL" sx={{ fontSize: "0.82rem" }}>
            Payouts Only
          </MenuItem>
        </Select>
      </Box>

      <Box sx={{ px: 3.5, py: 3 }}>
        {/* Summary cards */}
        {summary && (
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <SummaryCard
              label="Total Contributed"
              value={formatCurrency(summary.totalDeposits, summaryCurrency)}
              icon={<SavingsIcon fontSize="small" />}
              paletteColor={theme.palette.primary.main}
            />
            <SummaryCard
              label="Total Payouts"
              value={formatCurrency(summary.totalWithdrawals, summaryCurrency)}
              icon={<AccountBalanceWalletIcon fontSize="small" />}
              paletteColor={theme.palette.secondary.main}
            />
            <SummaryCard
              label="Net Balance"
              value={formatCurrency(summary.netBalance, summaryCurrency)}
              icon={<TrendingUpIcon fontSize="small" />}
              paletteColor={theme.palette.success.main}
            />
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Transactions */}
        {transactions.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <HistoryIcon
              sx={{ fontSize: "2.5rem", color: "text.disabled", mb: 1 }}
            />
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              No transactions found
            </Typography>
            <Typography
              sx={{ fontSize: "0.78rem", color: "text.disabled", mt: 0.5 }}
            >
              Your payment history will appear here once you make contributions
              or receive payouts.
            </Typography>
          </Box>
        ) : (
          <Box>
            {transactions.map((transaction, i) => (
              <Box key={transaction.id}>
                <TransactionRow transaction={transaction} />
                {i < transactions.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        )}

        {nextCursor && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCursor(nextCursor)}
              disabled={isFetching}
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                borderColor: "divider",
                color: "text.secondary",
                borderRadius: "8px",
                "&:hover": {
                  borderColor: "primary.main",
                  color: "primary.main",
                  bgcolor: "transparent",
                },
              }}
            >
              {isFetching ? (
                <CircularProgress size={14} sx={{ mr: 1 }} />
              ) : null}
              Load More
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
